/**
 * @file shop-format.ts
 * @description Даты в разделе «Магазины» кабинета платформы (phase19.28
 * этап А): по часам браузера, а не по поясу магазина вкладки — у платформы
 * своего пояса нет, а магазины в разных поясах. Здесь же — итог сборки
 * стартового сайта словами (экран итога и «Собрать ещё раз»).
 */

import { pluralRu } from "../plural";

/** «24.10.26, 12:40» — даты платформы (не по поясу магазина вкладки). */
export function formatPlatformDateTime(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** «24.10.26». */
export function formatPlatformDate(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

/** «5 мин назад», «3 ч назад», иначе дата. */
export function formatAgo(iso: string, now: Date = new Date()): string {
  const min = Math.floor((now.getTime() - Date.parse(iso)) / 60_000);
  if (!Number.isFinite(min)) return "";
  if (min < 1) return "только что";
  if (min < 60) return `${min} мин назад`;
  if (min < 24 * 60) return `${Math.floor(min / 60)} ч назад`;
  return formatPlatformDateTime(iso);
}

/** Почему стартовый сайт не собран (`reason` API) → слова. */
const STARTER_REASON_TEXT: Readonly<Record<string, string>> = {
  already_applied: "уже собран раньше — ничего не изменилось",
  template_invalid: "шаблон платформы не прошёл проверку",
  shop_not_found: "магазин не найден",
  failed: "сбой при сборке",
  not_implemented: "шаблон стартового сайта ещё не готов",
};

/** Итог сборки стартового сайта: «собран: 4 страницы-черновика» или «не собрался: …». */
export function siteStarterText(result: {
  applied: boolean;
  reason: string | null;
  pages?: ReadonlyArray<{ status: "created" | "skipped" }>;
}): { ok: boolean; text: string } {
  if (result.applied) {
    const created = result.pages?.filter((p) => p.status === "created").length ?? 0;
    const forms: [string, string, string] = ["страница-черновик", "страницы-черновика", "страниц-черновиков"];
    return { ok: true, text: created > 0 ? `Стартовый сайт собран: ${created} ${pluralRu(created, forms)}` : "Стартовый сайт собран" };
  }
  if (result.reason === "already_applied") {
    const total = result.pages?.length ?? 0;
    return {
      ok: true,
      text: total > 0 ? `Все ${total} ${pluralRu(total, ["страница", "страницы", "страниц"])} уже на месте` : `Стартовый сайт ${STARTER_REASON_TEXT.already_applied}`,
    };
  }
  return { ok: false, text: `Стартовый сайт не собрался: ${STARTER_REASON_TEXT[result.reason ?? ""] ?? "причина неизвестна"}` };
}
